import React, { useCallback, useEffect, useRef, useState } from 'react';
import { ArrowLeft, ExternalLink, Loader2, RefreshCw } from 'lucide-react';
import { BACKEND_URL } from '../../config';
import { qrFileName, qrReportUrl } from './qr';
import { LiveStatus, QrActions, QrCodeRow, errorMessage, useLivePolling, useQrDataUrl } from './qrShared';

/**
 * One QR code's live analytics: how often it is scanned and the reports filed
 * through it (`GET users/qr/:id/details`), refreshed every few seconds while
 * the page is visible. A scan is one browser session opening the code's link
 * (see pages/QRReport); a report counts when it was filed from that session
 * and matches the code's organisation/authority.
 */

type DayScans = { day: string; scans: number };

type QrReportRow = {
	id: number;
	title: string | null;
	createdAt: string;
	status: 'open' | 'in_progress' | 'closed';
	authorityName: string | null;
	reporterName: string | null;
};

type Details = { qrCode: QrCodeRow; scansByDay: DayScans[]; reports: QrReportRow[] };

const STATUS: Record<QrReportRow['status'], { label: string; cls: string }> = {
	open: { label: 'Open', cls: 'bg-blue-100 text-blue-700' },
	in_progress: { label: 'In progress', cls: 'bg-amber-100 text-amber-800' },
	closed: { label: 'Closed', cls: 'bg-emerald-100 text-emerald-700' },
};

const fmtDate = (iso: string) =>
	new Date(iso).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' });
const fmtDateTime = (iso: string) =>
	new Date(iso).toLocaleString('en-IN', { day: 'numeric', month: 'short', hour: 'numeric', minute: '2-digit' });
// `day` is an IST calendar day (YYYY-MM-DD); pin it to IST noon so the label
// can't slip a day in another browser timezone.
const fmtDay = (day: string) =>
	new Date(`${day}T12:00:00+05:30`).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', timeZone: 'Asia/Kolkata' });

const StatTile = ({ label, value, hint }: { label: string; value: string; hint?: string }) => (
	<div className='bg-white rounded-2xl border border-gray-100 shadow-sm p-5 text-left'>
		<div className='text-xs font-bold uppercase tracking-wide text-gray-500'>{label}</div>
		<div className='text-3xl font-extrabold text-gray-900 mt-1 tabular-nums'>{value}</div>
		{hint && <div className='text-xs text-gray-400 mt-1'>{hint}</div>}
	</div>
);

/**
 * Scans per day as thin bars on a shared baseline. One series, so no legend:
 * the heading names it. Each bar has a hover tooltip, and the same numbers are
 * in a screen-reader table underneath.
 */
const ScansChart = ({ data }: { data: DayScans[] }) => {
	const [hover, setHover] = useState<number | null>(null);
	const max = Math.max(1, ...data.map((d) => d.scans));
	const total = data.reduce((n, d) => n + d.scans, 0);

	return (
		<div className='bg-white rounded-2xl border border-gray-100 shadow-sm p-6 text-left'>
			<div className='flex items-baseline justify-between mb-4'>
				<h2 className='text-xs font-bold uppercase tracking-wide text-gray-500 m-0'>Scans per day, last {data.length} days</h2>
				<span className='text-sm text-gray-500 tabular-nums'>{total} total</span>
			</div>
			<div className='relative'>
				{/* Recessive gridline at the max, plus the baseline */}
				<div className='absolute left-8 right-0 top-0 border-t border-dashed border-gray-100' />
				<div className='absolute left-0 top-0 text-[11px] text-gray-400 tabular-nums -translate-y-1/2'>{max}</div>
				<div className='absolute left-0 bottom-0 text-[11px] text-gray-400 translate-y-1/2'>0</div>
				<div className='ml-8 h-40 flex items-end gap-[2px] border-b border-gray-200' onMouseLeave={() => setHover(null)}>
					{data.map((d, i) => (
						// Full-height hit target; the visible bar sits at its bottom.
						<div
							key={d.day}
							className='relative flex-1 h-full flex items-end cursor-default'
							onMouseEnter={() => setHover(i)}
						>
							<div
								className={`w-full rounded-t ${hover === i ? 'bg-blue-700' : 'bg-blue-600'}`}
								style={{ height: d.scans ? `${Math.max(2, (d.scans / max) * 100)}%` : 0 }}
							/>
							{hover === i && (
								<div
									className={`absolute bottom-full mb-2 z-10 whitespace-nowrap rounded-lg bg-gray-900 px-2.5 py-1.5 text-xs text-white shadow-lg ${
										i > data.length / 2 ? 'right-0' : 'left-0'
									}`}
								>
									<div className='font-semibold'>{fmtDay(d.day)}</div>
									<div className='tabular-nums text-gray-300'>
										{d.scans} {d.scans === 1 ? 'scan' : 'scans'}
									</div>
								</div>
							)}
						</div>
					))}
				</div>
				<div className='ml-8 mt-2 flex justify-between text-[11px] text-gray-400'>
					<span>{fmtDay(data[0].day)}</span>
					<span>{fmtDay(data[data.length - 1].day)}</span>
				</div>
			</div>
			<table className='sr-only'>
				<caption>Scans per day</caption>
				<thead>
					<tr>
						<th>Day</th>
						<th>Scans</th>
					</tr>
				</thead>
				<tbody>
					{data.map((d) => (
						<tr key={d.day}>
							<td>{fmtDay(d.day)}</td>
							<td>{d.scans}</td>
						</tr>
					))}
				</tbody>
			</table>
		</div>
	);
};

const QRCodeDetails = ({ qr, onBack }: { qr: QrCodeRow; onBack: () => void }) => {
	const [details, setDetails] = useState<Details | null>(null);
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState<string | null>(null);
	const [updatedAt, setUpdatedAt] = useState<number | null>(null);
	const [refreshFailed, setRefreshFailed] = useState(false);
	const inFlight = useRef(false);

	// `silent` is the background refresh: no spinner or error banner, and a
	// failure keeps the numbers on screen and only flips the status label.
	const load = useCallback(
		async (silent = false) => {
			if (inFlight.current) return;
			inFlight.current = true;
			if (!silent) {
				setLoading(true);
				setError(null);
			}
			try {
				const res = await fetch(`${BACKEND_URL}/users/qr/${qr.id}/details`, { credentials: 'include' });
				if (!res.ok) {
					if (silent) setRefreshFailed(true);
					else setError(await errorMessage(res));
					return;
				}
				setDetails(await res.json());
				setUpdatedAt(Date.now());
				setRefreshFailed(false);
			} catch {
				if (silent) setRefreshFailed(true);
				else setError('Could not reach the server.');
			} finally {
				inFlight.current = false;
				if (!silent) setLoading(false);
			}
		},
		[qr.id]
	);

	useEffect(() => {
		load();
	}, [load]);

	useLivePolling(() => load(true), true);

	// Prefer fresh numbers once loaded; the list row covers the first paint.
	const code = details?.qrCode ?? qr;
	const url = qrReportUrl(code.orgKey, code.authorityName, code.id);
	const dataUrl = useQrDataUrl(url, 512);
	const reportRate = code.uniqueScanners > 0 ? Math.round((code.reportCount / code.uniqueScanners) * 100) : null;

	return (
		<div className='max-w-6xl mx-auto px-4 pb-5 text-left'>
			<button
				onClick={onBack}
				className='inline-flex items-center gap-1.5 text-sm font-bold text-blue-700 hover:text-blue-900 mb-6'
			>
				<ArrowLeft className='w-4 h-4' /> All QR codes
			</button>

			{/* Header */}
			<div className='bg-white rounded-2xl border border-gray-100 shadow-sm p-6 mb-6 flex gap-6'>
				<div className='w-32 h-32 shrink-0 rounded-lg border border-gray-100 flex items-center justify-center'>
					{dataUrl ? (
						<img src={dataUrl} alt={`QR for ${code.label}`} className='w-full h-full' />
					) : (
						<Loader2 className='w-6 h-6 animate-spin text-gray-300' />
					)}
				</div>
				<div className='flex-1 min-w-0 flex flex-col gap-2'>
					<div className='flex items-start justify-between gap-3'>
						<h1 className='text-3xl font-extrabold text-blue-900 m-0 truncate'>{code.label}</h1>
						<button
							onClick={() => load()}
							disabled={loading}
							className='inline-flex items-center gap-1.5 rounded-xl border border-gray-200 px-3 h-9 text-sm font-bold text-gray-700 hover:bg-gray-50 disabled:opacity-50 shrink-0'
						>
							{loading ? <Loader2 className='w-4 h-4 animate-spin' /> : <RefreshCw className='w-4 h-4' />} Refresh
						</button>
					</div>
					<div className='flex flex-wrap items-center gap-1.5'>
						<span className='bg-sky-100 text-sky-700 px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wide'>
							{code.orgKey}
						</span>
						<span className='bg-indigo-100 text-indigo-700 px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wide'>
							{code.authorityName ?? (code.authorityId ? 'Unknown authority' : 'Any authority')}
						</span>
						{!code.isActive && (
							<span className='bg-gray-200 text-gray-700 px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wide'>
								Deleted
							</span>
						)}
					</div>
					<p className='text-sm text-gray-500 m-0'>
						{code.placement || <span className='italic text-gray-400'>No placement recorded</span>}
						<span className='mx-1.5 text-gray-300'>•</span>
						Created {fmtDate(code.createdAt)}
					</p>
					<div className='mt-auto flex flex-wrap items-center justify-between gap-3'>
						<QrActions url={url} fileName={qrFileName(code.orgKey, code.label)} title={code.label} />
						<LiveStatus updatedAt={updatedAt} failed={refreshFailed} />
					</div>
				</div>
			</div>

			{error && (
				<div className='mb-6 py-1.5 px-4 rounded-xl bg-red-50 text-red-600 border border-red-100 font-semibold text-center'>
					{error}
				</div>
			)}

			{/* Headline numbers */}
			<div className='grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6'>
				<StatTile label='Scans' value={String(code.scanCount)} hint='Browser sessions that opened the code' />
				<StatTile label='Unique scanners' value={String(code.uniqueScanners)} hint='Distinct devices' />
				<StatTile label='Reports filed' value={String(code.reportCount)} />
				<StatTile
					label='Scan → report'
					value={reportRate == null ? '–' : `${reportRate}%`}
					hint={code.lastScanAt ? `Last scan ${fmtDateTime(code.lastScanAt)}` : 'Not scanned yet'}
				/>
			</div>

			{details && <div className='mb-6'>{details.scansByDay.length > 0 && <ScansChart data={details.scansByDay} />}</div>}

			{/* Reports filed through this code */}
			<div className='bg-white rounded-2xl border border-gray-100 shadow-sm p-6'>
				<h2 className='text-xs font-bold uppercase tracking-wide text-gray-500 m-0 mb-4'>
					Reports filed through this code{details ? ` (${details.reports.length})` : ''}
				</h2>
				{loading && !details ? (
					<div className='flex justify-center py-10'>
						<Loader2 className='w-8 h-8 animate-spin text-blue-600 opacity-50' />
					</div>
				) : !details || details.reports.length === 0 ? (
					<p className='text-gray-500 font-medium py-6 m-0'>No reports have been filed through this code yet.</p>
				) : (
					<div className='overflow-x-auto'>
						<table className='w-full text-sm'>
							<thead>
								<tr className='text-left text-xs font-bold uppercase tracking-wide text-gray-500 border-b border-gray-100'>
									<th className='py-2 pr-4 font-bold'>Filed</th>
									<th className='py-2 pr-4 font-bold'>Title</th>
									<th className='py-2 pr-4 font-bold'>Authority</th>
									<th className='py-2 pr-4 font-bold'>Reporter</th>
									<th className='py-2 pr-4 font-bold'>Status</th>
									<th className='py-2' />
								</tr>
							</thead>
							<tbody>
								{details.reports.map((r) => (
									<tr key={r.id} className='border-b border-gray-50 last:border-0 hover:bg-gray-50'>
										<td className='py-2.5 pr-4 text-gray-500 whitespace-nowrap'>{fmtDateTime(r.createdAt)}</td>
										<td className='py-2.5 pr-4 font-semibold text-gray-900 max-w-xs truncate' title={r.title ?? ''}>
											{r.title || <span className='italic text-gray-400 font-normal'>Untitled</span>}
										</td>
										<td className='py-2.5 pr-4 text-gray-700'>{r.authorityName ?? '–'}</td>
										<td className='py-2.5 pr-4 text-gray-700'>{r.reporterName ?? '–'}</td>
										<td className='py-2.5 pr-4'>
											<span
												className={`px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wide whitespace-nowrap ${STATUS[r.status].cls}`}
											>
												{STATUS[r.status].label}
											</span>
										</td>
										<td className='py-2.5 text-right'>
											<a
												href={`/getreport/${r.id}`}
												target='_blank'
												rel='noreferrer'
												className='inline-flex items-center gap-1 text-xs font-bold text-blue-700 hover:text-blue-900'
											>
												Open <ExternalLink className='w-3.5 h-3.5' />
											</a>
										</td>
									</tr>
								))}
							</tbody>
						</table>
					</div>
				)}
			</div>
		</div>
	);
};

export default QRCodeDetails;
